import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Nyheter from './pages/Nyheter';
import { useErLeder, ROLLER, paPause, erUtviklerNavn } from './employeeAccess';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


function EmployeeList() {
    const [employees, setEmployees] = useState([]); // Sett tom array som initial verdi
    const [activeTab, setActiveTab] = useState('ansatte');
    const navigate = useNavigate();
    // Bare leder ser tilgangen og kan legge til og endre ansatte (skille i UI, se employeeAccess.js).
    const leder = useErLeder();

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/employees`);
                if (!response.ok) {
                    throw new Error('Feil ved henting av ansatte');
                }
                const data = await response.json();
                setEmployees(data); // Oppdaterer employees med data fra API
            } catch (error) {
                console.error('Feil ved henting av ansatte:', error);
            }
        };

        fetchEmployees();
    }, []);

    const handleSelectEmployee = (employeeId) => {
        navigate(`/employee-change/${employeeId}`); // Naviger til EmployeeChange for å endre en ansatt
    };

    const handleAddEmployee = () => {
        navigate('/employee-form'); // Naviger til EmployeeForm for å registrere nye ansatte
    };

    return (
        <div className="max-w-5xl mx-auto py-8 bg-white shadow-lg rounded-lg p-6 mb-4">
            <div className="flex gap-2 mb-6 border-b border-gray-200">
                <button
                    onClick={() => setActiveTab('ansatte')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'ansatte' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Ansatte
                </button>
                <button
                    onClick={() => setActiveTab('nyheter')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'nyheter' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                >
                    Nyheter
                </button>
            </div>

            {activeTab === 'nyheter' ? (
                <Nyheter />
            ) : (
                <>
                    {/* Flexbox for å plassere overskrift og knapp på samme rad */}
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-semibold">Ansatte</h2>
                        {/* Knapp for å legge til nye ansatte (bare leder) */}
                        {leder && (
                            <button
                                onClick={handleAddEmployee}
                                className="bg-blue-700 text-white px-4 py-2 rounded hover:bg-blue-800"
                            >
                                Legg til ansatt
                            </button>
                        )}
                    </div>
                    {!leder && (
                        <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                            Bare en leder kan legge til og endre ansatte. Velg en leder øverst.
                        </p>
                    )}
                    {employees.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                              <thead>
                                  <tr className="bg-gray-100">
                                      <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600">Navn</th>
                                      <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600">Telefon</th>
                                      <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600 hidden md:table-cell">E-post</th> {/* Skjul på små skjermer */}
                                      {leder && <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600">Tilgang og roller</th>}
                                  </tr>
                              </thead>
                              <tbody>
                                  {employees.map((employee) => (
                                      <tr
                                          key={employee._id}
                                          className={`${leder ? 'hover:bg-gray-50 cursor-pointer' : ''} ${paPause(employee) || employee.butikk === 'Ingen' ? 'bg-slate-100' : ''}`}
                                          onClick={leder ? () => handleSelectEmployee(employee._id) : undefined} // Leder: gå til EmployeeChange
                                      >
                                          <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                                              {leder ? (
                                                  <button
                                                      type="button"
                                                      onClick={(e) => { e.stopPropagation(); handleSelectEmployee(employee._id); }}
                                                      className="text-left font-medium text-blue-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                                                      aria-label={`Endre ${employee.navn}`}
                                                  >
                                                      {employee.navn}
                                                  </button>
                                              ) : employee.navn}
                                              {paPause(employee) && (
                                                  <span className="ml-2 whitespace-nowrap rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900">På pause</span>
                                              )}
                                              {employee.butikk === 'Ingen' && (
                                                  <span className="ml-2 whitespace-nowrap rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-700">Ikke aktiv</span>
                                              )}
                                          </td>
                                          <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">{employee.telefon}</td>
                                          <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700 hidden md:table-cell">{employee.epost}</td> {/* Skjul på små skjermer */}
                                          {leder && (
                                              <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                                                  <div className="flex flex-wrap gap-1">
                                                      {employee.tilgang === 'leder' ? (
                                                          <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-800">Leder</span>
                                                      ) : (
                                                          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">Ansatt</span>
                                                      )}
                                                      {ROLLER.filter((r) => employee.roller?.includes(r.id)).map((r) => (
                                                          <span key={r.id} className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">{r.navn}</span>
                                                      ))}
                                                      {/* Hardkodet, ikke en rolle som kan velges (src/employeeAccess.js). */}
                                                      {erUtviklerNavn(employee.navn) && (
                                                          <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-800">Utvikler</span>
                                                      )}
                                                  </div>
                                              </td>
                                          )}
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                        </div>
                    ) : (
                        <p className="text-center text-gray-600">Ingen ansatte funnet</p>
                    )}
                </>
            )}
        </div>
    );
}

export default EmployeeList;
